import { useMemo } from 'react';
import { ForecastModel } from '@app/common/static-data/models';

import { PlottableForecast } from '../types';

export function useAlignedForecasts(
  forecasts: Record<ForecastModel, PlottableForecast>,
  selectedModels: ForecastModel[],
  endHour: number,
) {
  return useMemo(() => {
    // Filter the forecasts to only include the selected models
    const selectedForecasts = Object.fromEntries(
      Object.entries(forecasts).filter(([modelId]) =>
        selectedModels.includes(modelId as ForecastModel),
      ),
    ) as Record<ForecastModel, PlottableForecast>;

    if (Object.keys(selectedForecasts).length === 0) return {};

    // Find the earliest starting forecast
    const earliestForecast = Object.values(selectedForecasts).reduce((earliest, current) =>
      !earliest?.time
        ? current
        : !current?.time
          ? earliest
          : new Date(current.time[0]).getTime() < new Date(earliest.time[0]).getTime()
            ? current
            : earliest,
    );
    const latestForecast = Object.values(selectedForecasts).reduce((latest, current) =>
      !latest?.time
        ? current
        : !current?.time
          ? latest
          : new Date(current.time[current.time.length - 1]).getTime() >
              new Date(latest.time[latest.time.length - 1]).getTime()
            ? current
            : latest,
    );

    // Find the earliest start time
    const earliestStartTime = new Date(earliestForecast.time[0]).getTime();
    const latestEndTime = new Date(latestForecast.time[latestForecast.time.length - 1]).getTime();

    // Calculate the maximum available duration from the earliest start time
    // compute as difference between latest end time and earliest start time
    // and convert to hours
    const maxAvailableDuration = (latestEndTime - earliestStartTime) / (1000 * 60 * 60);

    // Use the minimum of selected horizon and max available duration
    const maxHorizon = Math.min(endHour, maxAvailableDuration);
    const endTime = new Date(earliestStartTime + maxHorizon * 60 * 60 * 1000);

    // Align the forecasts to the reference end time
    return Object.fromEntries(
      Object.entries(selectedForecasts).map(([modelId, forecast]) => {
        // Find the last valid index for this specific model
        // If not found this means that all times of the forecast are before the endTime
        // so we use the last index of the forecast
        const modelEndIndexRaw = forecast.time.findIndex((time) => time >= endTime);
        const modelEndIndex = modelEndIndexRaw !== -1 ? modelEndIndexRaw : forecast.time.length - 1;
        return [
          modelId,
          {
            time: forecast.time.slice(0, modelEndIndex + 1),
            ...Object.fromEntries(
              Object.entries(forecast)
                .filter(([key]) => key !== 'time')
                .map(([key, values]) => [
                  key,
                  Array.isArray(values) ? values.slice(0, modelEndIndex + 1) : values,
                ]),
            ),
          },
        ];
      }),
    ) as Record<ForecastModel, PlottableForecast>;
  }, [forecasts, selectedModels, endHour]);
}
